import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { PageHero } from "../components/common/PageHero";
import { FinalCta } from "../components/common/FinalCta";
import { Reveal } from "../components/motion/Reveal";
import { IMAGES } from "../data/site";
import { ytId, igId } from "../data/defaults";
import { useSiteData, useC, fileUrl } from "../context/SiteData";

const POSTER_SLOTS = ["videoPoster", "video2", "video3"];
const POSTER_FALLBACKS = [IMAGES.videoPoster, IMAGES.roomExtra, IMAGES.building];

const Videolar = () => {
  const [open, setOpen] = useState(null);
  const { images, loaded } = useSiteData();
  const c = useC();

  const videos = c.videos.map((v, i) => ({
    ...v,
    poster: images[POSTER_SLOTS[i % POSTER_SLOTS.length]]
      ? fileUrl(images[POSTER_SLOTS[i % POSTER_SLOTS.length]])
      : loaded
        ? POSTER_FALLBACKS[i % POSTER_FALLBACKS.length]
        : null,
    yt: ytId(v.url),
    ig: igId(v.url),
  }));

  return (
    <>
      <PageHero
        title={c.ph_videolar_title}
        subtitle={c.ph_videolar_subtitle}
        desc={c.ph_videolar_desc}
      />
      <section data-testid="videolar-list" className="section-y">
        <div className="container-x grid gap-8 lg:grid-cols-1">
          {videos.map((v, i) => (
            <Reveal key={`${v.title}-${i}`} delay={i * 0.08}>
              <button
                onClick={() => setOpen(i)}
                data-testid={`video-card-${i}`}
                className="group relative block w-full overflow-hidden text-left"
              >
                <div className="aspect-video w-full overflow-hidden">
                  {v.poster ? (
                    <img
                      src={v.poster}
                      alt={v.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                    />
                  ) : (
                    <div className="h-full w-full animate-pulse bg-ink-800" />
                  )}
                </div>
                <div className="absolute inset-0 bg-ink-950/35 transition-colors duration-300 group-hover:bg-ink-950/50" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full border border-cream-50/40 bg-ink-950/40 text-cream-50 backdrop-blur-sm transition-all duration-300 group-hover:scale-105 group-hover:bg-cream-50 group-hover:text-ink-900 sm:h-20 sm:w-20">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13l11-6.5-11-6.5z" /></svg>
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 flex w-full items-end justify-between p-5 sm:p-7">
                  <p className="text-lg font-bold tracking-tight text-cream-50 sm:text-2xl">{v.title}</p>
                  {!v.yt && !v.ig && (
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cream-50/70">Yakında</span>
                  )}
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-950/95 p-5"
            onClick={() => setOpen(null)}
            data-testid="video-modal"
          >
            <button
              onClick={() => setOpen(null)}
              data-testid="video-modal-close"
              aria-label="Kapat"
              className="absolute right-5 top-5 text-cream-50/70 transition-colors hover:text-cream-50"
            >
              <X size={28} />
            </button>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className={videos[open].yt ? "w-full max-w-4xl" : videos[open].ig ? "w-full max-w-md" : "max-w-lg text-center"}
              onClick={(e) => e.stopPropagation()}
            >
              {videos[open].yt ? (
                <div className="aspect-video w-full overflow-hidden border border-cream-50/10">
                  <iframe
                    title={videos[open].title}
                    src={`https://www.youtube.com/embed/${videos[open].yt}?autoplay=1&rel=0`}
                    className="h-full w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="video-embed"
                  />
                </div>
              ) : videos[open].ig ? (
                <div className="w-full overflow-hidden border border-cream-50/10 bg-cream-50">
                  <iframe
                    title={videos[open].title}
                    src={`https://www.instagram.com/p/${videos[open].ig}/embed`}
                    className="h-[70vh] w-full"
                    frameBorder="0"
                    scrolling="no"
                    data-testid="video-embed"
                  />
                </div>
              ) : (
                <>
                  <p className="text-2xl font-bold tracking-tight text-cream-50 sm:text-3xl">{videos[open].title}</p>
                  <p className="mt-4 text-base leading-relaxed text-cream-50/65">
                    {c.video_soon_text}
                  </p>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <FinalCta />
    </>
  );
};

export default Videolar;
